import React from "react";

// types
import type { MessageWithDirectives } from "../../types/api/general";
import type { DirectiveType } from "../../types/api/moneyTransfer";
import { ActionLink } from "./ActionLink";

interface MessageWithDirectivesTextProps {
  content: MessageWithDirectives;
  /** directive key -> handler for directives with no url (in-page actions) */
  actions?: Record<string, () => void>;
  className?: string;
  imgSrc?: string;
}

/** Resolve a directive's placeholder key and visible label across the wire
 *  (directiveType/directiveText) and normalized (type/text) field names. */
const directiveKey = (d: DirectiveType): string => d.directiveType ?? d.type;
const directiveLabel = (d: DirectiveType): string => d.directiveText ?? d.text;

/**
 * Renders a MessageWithDirectives: an HTML `message` string with {KEY}
 * placeholders, each replaced in place by a clickable element for the matching
 * directive. A directive with a `url` renders as a link; one without renders as
 * a button bound to actions[key] (e.g. toggling an inline panel).
 *
 * Splits the message on each token and interleaves raw-HTML spans (for the
 * surrounding copy: <strong>, <br/>) with React directive elements, so the
 * directive stays where the server placed it in the sentence.
 */
const MessageWithDirectivesText = ({
  content,
  actions,
  className,
  imgSrc,
}: MessageWithDirectivesTextProps): React.ReactElement => {
  const { message, directives } = content;

  // key -> directive, and the escaped {KEY} tokens to split on. Keys are
  // resolved to definite strings and empty ones dropped.
  const byKey = new Map<string, DirectiveType>();
  const tokens: string[] = [];
  for (const d of directives) {
    const key = directiveKey(d);
    if (!key) continue;
    byKey.set(key, d);
    tokens.push(`\\{${key}\\}`);
  }

  if (!tokens.length) {
    return (
      <span
        className={className}
        dangerouslySetInnerHTML={{ __html: message }}
      />
    );
  }

  const splitter = new RegExp(`(${tokens.join("|")})`, "g");
  const parts = message.split(splitter);

  return (
    <span className={className}>
      {parts.map((part, i) => {
        const tokenMatch = /^\{(.+)\}$/.exec(part);
        const directive = tokenMatch ? byKey.get(tokenMatch[1]) : undefined;

        if (directive) {
          const label = directiveLabel(directive);
          if (directive.url) {
            return (
              <a
                key={i}
                href={directive.url}
                className="text-blue-700 no-underline bold"
              >
                {label}
              </a>
            );
          }
          return (
            <ActionLink
              key={i}
              classes="text-blue-700 no-underline bold flex-row align-items-center gap-8 mt-8"
              clickFunc={() => actions?.[directiveKey(directive)]?.()}
            >
              <div className="flex-row align-items-center">
                <img src={imgSrc} alt="" />
                {label}
              </div>
            </ActionLink>
          );
        }

        return (
          <span
            key={i}
            dangerouslySetInnerHTML={{ __html: part }}
          />
        );
      })}
    </span>
  );
};

export default MessageWithDirectivesText;